const SIGN_UP_NOT_AVAILABLE_MESSAGE = "Signup is not available for this account type";

// The PreSignUp Lambda embeds an extensible hex bitmap of the account's usable
// sign-in factors alongside the identifier. Only bit 0 (a usable password) is
// defined today; decode defensively so future bits don't break the parse.
const CAPABILITY_BIT = {
  usablePassword: 0x01,
} as const;

interface ErrorMessageParams {
  errorMessage: string;
}

export function isSignUpNotAvailableErrorMessage({ errorMessage }: ErrorMessageParams): boolean {
  return errorMessage.includes(SIGN_UP_NOT_AVAILABLE_MESSAGE);
}

export function parseCognitoUsernameFromSignUpErrorMessage({
  errorMessage,
}: ErrorMessageParams): string | undefined {
  if (!isSignUpNotAvailableErrorMessage({ errorMessage })) {
    return undefined;
  }

  return /Identifier:\s*"([^"]+)"/.exec(errorMessage)?.[1];
}

/**
 * Decodes the `Capabilities: "0x.."` hex bitmap from the PreSignUp rejection.
 * Returns 0 when the field is absent (older Lambda, or no bits set) or malformed
 * so callers treat "unknown" as "no capabilities" rather than an error.
 */
export function parseCapabilitiesFromSignUpErrorMessage({
  errorMessage,
}: ErrorMessageParams): number {
  if (!isSignUpNotAvailableErrorMessage({ errorMessage })) {
    return 0;
  }

  const hex = /Capabilities:\s*"(?:0x)?([0-9a-fA-F]+)"/.exec(errorMessage)?.[1];
  if (!hex) {
    return 0;
  }

  const parsed = Number.parseInt(hex, 16);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function capabilitiesIncludeUsablePassword(capabilities: number): boolean {
  // eslint-disable-next-line no-bitwise -- decoding a bitmap flag
  return (capabilities & CAPABILITY_BIT.usablePassword) !== 0;
}
